import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Tabs, Tab, Checkbox } from '@mui/material';
import { useDocs } from '../items/hooks/HDocs';
import { API_BASE } from '../../config/constants';

interface FileItem {
    name: string;
    blobName: string;
    url: string;
    contentType?: string;
    size?: number;
    lastModified?: string;
}

interface Folder {
    key: string;
    title: string;
    files: FileItem[];
}

interface Props {
    objectId: string | number | null;
    canUpload?: boolean;
    onFileDrop?: (file: File, objectId: string | number) => Promise<void> | void;
}

export default function ObjectDataroom({ objectId, canUpload = false, onFileDrop }: Props) {
    const { fetchObjectDataroom } = useDocs();
    const [folders, setFolders] = useState<Folder[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [active, setActive] = useState(0);
    const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!objectId) return;
        setLoading(true);
        setError(null);
        fetchObjectDataroom(objectId)
            .then((res) => {
                if (res && res.success) setFolders(res.folders || []);
                else setFolders([]);
            })
            .catch((err) => setError(err.message || 'Failed to load dataroom'))
            .finally(() => setLoading(false));
    }, [objectId, fetchObjectDataroom]);

    const handleDrop = async (ev: React.DragEvent, targetKey?: string) => {
        ev.preventDefault();
        const file = ev.dataTransfer.files?.[0];
        if (!file || !objectId) return;
        await uploadFile(file, targetKey);
    };

    const folderKeyToPath = (key: string) => {
        switch (key) {
            case 'DD':
                return 'due-diligence';
            case 'FS':
                return 'factsheets';
            case 'PIC':
            default:
                return 'pictures';
        }
    };

    const isImageFile = (file: File) => {
        const ct = file.type || '';
        if (ct.startsWith('image/')) return true;
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
    };

    const uploadFile = async (file: File, targetKey?: string) => {
        if (!objectId) return;
        setUploading(true);
        setError(null);
        try {
            // Determine target folder: if file is image, always go to pictures
            const finalKey = isImageFile(file) ? 'PIC' : (targetKey || folders[active]?.key || 'FS');
            const folderPath = folderKeyToPath(finalKey);

            if (onFileDrop) {
                // Delegate to parent upload handler; parent may or may not respect folder
                await onFileDrop(file, objectId);
            } else {
                const fd = new FormData();
                fd.append('file', file);
                fd.append('object_id', String(objectId));
                fd.append('container_name', 'docs');
                fd.append('directory_name', `objects/${objectId}/${folderPath}`);

                const res = await fetch(`${API_BASE}/api/objects/create-object-media`, {
                    method: 'POST',
                    body: fd,
                });
                if (!res.ok) throw new Error('Upload failed');
                const json = await res.json();
                if (!json.success) throw new Error(json.message || 'Upload failed');
            }

            // reload
            setLoading(true);
            const res = await fetchObjectDataroom(objectId);
            setFolders(res.folders || []);
        } catch (err: any) {
            setError(err?.message || 'Upload failed');
        } finally {
            setUploading(false);
            setLoading(false);
        }
    };

    const handleFileChange = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const file = ev.target.files?.[0];
        if (file) await uploadFile(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleAddClick = () => fileInputRef.current?.click();

    const handleDeleteSelected = async () => {
        if (!objectId || selectedFiles.size === 0) return;
        setDeleting(true);
        setError(null);
        try {
            const urls = Array.from(selectedFiles);
            for (const u of urls) {
                const payload = {
                    doc_url: u,
                    container_name: 'docs',
                    directory_name: `objects/${objectId}`,
                };

                const res = await fetch(`${API_BASE}/api/objects/delete-document-by-id`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                if (!res.ok) {
                    const txt = await res.text();
                    throw new Error(txt || 'Failed to delete');
                }
            }

            // reload
            setLoading(true);
            const res = await fetchObjectDataroom(objectId);
            setFolders(res.folders || []);
            setSelectedFiles(new Set());
        } catch (err: any) {
            setError(err?.message || 'Delete failed');
        } finally {
            setDeleting(false);
            setLoading(false);
        }
    };

    const toggleFile = (url: string) => {
        setSelectedFiles((prev) => {
            const next = new Set(prev);
            if (next.has(url)) next.delete(url);
            else next.add(url);
            return next;
        });
    };

    if (!objectId) return <Box sx={{ color: '#666' }}>No object selected.</Box>;
    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>;
    if (error) return <Box sx={{ color: '#d32f2f', py: 2 }}>{error}</Box>;

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Tabs value={active} onChange={(_, v) => setActive(v)}>
                    {folders.map((f, idx) => (
                        <Tab key={f.key} label={`${f.title} (${(f.files || []).length})`} />
                    ))}
                </Tabs>
            </Box>

            <Box onDragOver={(e) => e.preventDefault()} onDrop={(e) => handleDrop(e, folders[active]?.key)}>
                {canUpload && (
                    <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <input ref={fileInputRef} type='file' style={{ display: 'none' }} onChange={handleFileChange} />
                        <Button size='small' variant='outlined' onClick={handleAddClick} disabled={uploading || loading}>{uploading ? 'Uploading...' : 'Add'}</Button>
                        <Button size='small' variant='outlined' color='error' onClick={handleDeleteSelected} disabled={deleting || selectedFiles.size === 0}>{deleting ? 'Deleting...' : `Delete (${selectedFiles.size})`}</Button>
                    </Box>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                    {(folders[active]?.files || []).map((doc, idx) => {
                        const isImage = doc.contentType?.startsWith('image/');
                        return (
                            <Box key={idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                                <Box sx={{ position: 'relative' }}>
                                    <Checkbox
                                        size='small'
                                        sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, background: 'rgba(255,255,255,0.8)' }}
                                        checked={selectedFiles.has(doc.url)}
                                        onChange={() => toggleFile(doc.url)}
                                    />
                                    {isImage ? (
                                        <img src={doc.url} alt={doc.name} style={{ width: '100%', height: 140, objectFit: 'cover' }} />
                                    ) : (
                                        <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
                                            <Box sx={{ textAlign: 'center' }}>{(doc.name || '').split('.').pop()?.toUpperCase() || 'FILE'}</Box>
                                        </Box>
                                    )}
                                </Box>
                                <Box sx={{ p: 1 }}>
                                    <Box sx={{ fontSize: 13, fontWeight: 600 }}>{doc.name}</Box>
                                    <Box sx={{ fontSize: 11, color: '#777' }}>{(doc.size || 0) ? `${Math.round((doc.size||0)/1024)} KB` : ''}</Box>
                                    <Box sx={{ mt: 1 }}>
                                        <Button size='small' onClick={() => window.open(doc.url, '_blank')}>Download</Button>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}
